import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks';
import { hasMinimumRole, type Permission, type UserRole } from '@bstock/shared';
import { Loader } from '@/components/ui';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Permission granulaire requise (ex: 'inventories.validate') */
  requiredPermission?: Permission;
  /** Role minimum requis (alternative a requiredPermission) */
  minRole?: UserRole;
  /** Require SuperAdmin access (bypass all other checks) */
  requireSuperAdmin?: boolean;
}

export function ProtectedRoute({ children, requiredPermission, minRole, requireSuperAdmin }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const hasPermission = usePermission(requiredPermission ?? 'stock.read');

  // Afficher un loader pendant la verification de l'authentification
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader size="lg" />
      </div>
    );
  }

  // Rediriger vers login si non authentifie
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Verifier si SuperAdmin requis
  if (requireSuperAdmin && !user?.isSuperAdmin) {
    return <Navigate to="/403" replace />;
  }

  // Si SuperAdmin requis et passe, on skip les autres checks
  if (requireSuperAdmin) {
    return <>{children}</>;
  }

  // Verifier la permission granulaire si specifiee
  if (requiredPermission && !hasPermission) {
    return <Navigate to="/403" replace />;
  }

  // Verifier le role minimum si specifie
  if (minRole && user) {
    const userRole = user.currentBrand?.bstockRole ?? user.currentBrand?.role;
    // SuperAdmin bypass
    if (!user.isSuperAdmin && userRole && !hasMinimumRole(userRole, minRole)) {
      return <Navigate to="/403" replace />;
    }
  }

  return <>{children}</>;
}
